import { computed, DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthFacade } from '@core/auth/auth-facade';
import { ClockService } from '@core/clock/clock.service';
import { TenantFacade } from '@core/data/tenant/tenant-facade';
import {
  AppShellNavLinkVm,
  AppShellSidenavViewModel,
  AppShellTenantOptionVm,
} from './app-shell.vm';
import { formatLocalTime, formatShellTopBarDate } from '@shared/utils/time-format.utils';
import { SiteFacade } from '@core/data/site/site-facade';
import { LocationFacade } from '@core/data/location/location-facade';
import {IconType} from "../../../assets/icons/icon-manifest";

export interface TabConfig {
  label: string;
  route: (string | number | null)[];
}

const MAIN_NAV_LINKS: readonly AppShellNavLinkVm[] = [
  { label: 'Dashboard', route: '/dashboard', iconKey: 'dashboard' },
  { label: 'Sites', route: '/sites', iconKey: 'site' },
  { label: 'Alerts', route: '/alerts', iconKey: 'alerts' },
  { label: 'Inspections', route: '/inspections', iconKey: 'image' },
  {
    label: 'Trending visualisation',
    route: '/trending-visualisation',
    iconKey: 'trends',
  },
  { label: 'Fleet management', route: '/robot-fleet', iconKey: 'robot' },
];

const SUPPORT_NAV: AppShellNavLinkVm = {
  label: 'Support',
  route: '/support',
  iconKey: 'help',
};

const SITE_ICON_KEY: IconType = 'site';
const TENANT_MENU_TRIGGER_ICON_KEY: IconType = 'chevron_down';
const LOCATION_ICON_KEY: IconType = 'location';

@Injectable()
export class AppShellFacade {
  private readonly authFacade = inject(AuthFacade);
  private readonly tenantFacade = inject(TenantFacade);
  private readonly siteFacade = inject(SiteFacade);
  private readonly locationFacade = inject(LocationFacade);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly clockService = inject(ClockService);

  private routeTitle = signal<string>('AIDA');
  private readonly tenants: Signal<AppShellTenantOptionVm[]> = computed(() =>
    this.tenantFacade.accessibleTenants().map((it) => ({
      id: it.uuid,
      label: it.name ?? '',
    }))
  );

  private readonly _sites = this.siteFacade.sites;
  private readonly _currentUrl = signal<string>(this.router.url);

  public normalizeUrl(url: string): string {
    const withoutHash = url.split('#', 1)[0] ?? '';
    const pathOnly = withoutHash.split('?', 1)[0] ?? '';
    if (pathOnly.length > 1 && pathOnly.endsWith('/')) {
      return pathOnly.slice(0, -1);
    }
    return pathOnly;
  }

  readonly activeSiteId = this.siteFacade.activeSiteId;

  readonly currentUrl: Signal<string> = this._currentUrl.asReadonly();

  readonly formattedLocalTime: Signal<string> = computed(() => {
    const siteId = this.activeSiteId();
    return formatLocalTime(
      this.clockService.tick(),
      !!siteId
        ? (this._sites()[siteId - 1]?.timezone ?? 'UTC')
        : Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  });

  /** Readable long-form date for the top-bar header, updated every minute. */
  readonly formattedDate: Signal<string> = computed(() =>
    formatShellTopBarDate(this.clockService.tick())
  );

  readonly vm: Signal<AppShellSidenavViewModel> = computed(() => {
    const tenantConfigVm = this.tenantFacade.tenantVm();
    const selectedTenant = this.tenantFacade.currentTenant();
    const selectedUser = this.authFacade.user();
    const mainNavLinks = MAIN_NAV_LINKS;
    const locationContextPrefix = [
      '/sites',
      this.siteFacade.activeSiteId(),
      'locations',
      this.locationFacade.locationId(),
    ];
    const availableTabRoutes: TabConfig[] = this.hasLocationContext()
      ? [
        {
          label: 'Hall Dashboard',
          route: [...locationContextPrefix, 'dashboard']
        },
          {
            label: 'Media Visualizer',
            route: [...locationContextPrefix, 'media'],
          },
          { label: 'Hall Map', route: [...locationContextPrefix, 'hall-map'] },
        ]
      : [];

    const headerTitle = tenantConfigVm?.name?.trim() ?? '';

    return {
      tenantLogoUrlMain: tenantConfigVm?.branding?.logoUrlMain ?? null,
      tenantLogoUrlSmall: tenantConfigVm?.branding?.logoUrlSmall ?? null,
      headerTitle,
      selectedTenantId: selectedTenant?.uuid ?? null,
      tenants: this.tenants(),
      showTenantMenuTrigger: this.tenants().length > 1,
      tenantMenuTriggerIconKey: TENANT_MENU_TRIGGER_ICON_KEY,
      mainNavLinks: [...mainNavLinks],
      sites: this._sites().map((site) => ({
        id: site.id,
        label: site.name,
        locations: site.locations.map((location) => ({
          id: location.id,
          label: location.name,
          iconKey: LOCATION_ICON_KEY,
        })),
      })),
      supportNav: { ...SUPPORT_NAV },
      trailingChevronIconKey: 'chevron_right',
      logoutIconKey: 'logout',
      userAvatarIconKey: 'person',
      userName: selectedUser?.name ?? 'Unknown User',
      userEmail: selectedUser?.email ?? 'unknown@example.com',
      routeTitle: this.routeTitle(),
      headerAlertIconKey: 'newsfeed',
      headerInboxIconKey: 'newsfeed_active',
      sitesIconKey: SITE_ICON_KEY,
      availableTabRoutes,
    };
  });

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        const navigationEvent = event as NavigationEnd;
        this._currentUrl.set(navigationEvent.urlAfterRedirects);
        this.updateTitle();
      });
  }

  private updateTitle() {
    let route = this.router.routerState.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    this.routeTitle.set(route.snapshot.data['headerTitle'] ?? 'AIDA');
  }

  switchTenant(tenantId: string): void {
    if (!this.tenants().filter((it) => it.id === tenantId)) {
      return;
    }

    if (this.tenantFacade.currentTenant()?.uuid === tenantId) {
      return;
    }
    void this.tenantFacade.setCurrentTenant(tenantId);
  }

  navigate(route: string): void {
    void this.router.navigateByUrl(route);
  }

  openSite(siteId: number): void {
    this.siteFacade.openSite(siteId);
  }

  openLocation(siteId: number, locationId: number): void {
    this.locationFacade.openLocation(siteId, locationId);
  }

  logout(): void {
    this.authFacade.logout().subscribe();
  }

  private hasLocationContext(): boolean {
    return this.siteFacade.activeSiteId() !== null && this.locationFacade.locationId() !== null;
  }
}
